import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, ChevronLeft, Star, MessageCircle, BadgeCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { DIFFICULTY_COLORS, DataSourceBadge, ReactionButtons, SpotMeta, openDirections, LoadingDots, formatHour, getShapeLabel, formatTideTime } from './constants';
import SpotOverviewTab from './SpotOverviewTab';
import SpotForecastTab from './SpotForecastTab';
import SpotSpitcastTab from './SpotSpitcastTab';
import SpotTidesTab from './SpotTidesTab';
import SpotDailyTab from './SpotDailyTab';
import { authFetch, API } from '../../utils/api';


export default function SpotDetail({ name, isStarred, myReaction, onStar, onReact, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Lazy-loaded tab data
  const [tabData, setTabData] = useState({ spitcast: null, noaa: null, forecast: null });
  const [tabLoading, setTabLoading] = useState({ spitcast: false, noaa: false, forecast: false });
  const tabFetched = React.useRef({ spitcast: false, noaa: false, forecast: false });

  // Initial fast load (lazy=true)
  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/api/spots/detail?name=${encodeURIComponent(name)}&lazy=true`);
        if (res.ok) setData(await res.json());
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [name]);

  // Lazy-load tab data on tab switch
  const loadTabData = useCallback(async (tab) => {
    if (tabFetched.current[tab] || tabLoading[tab]) return;
    setTabLoading(prev => ({ ...prev, [tab]: true }));
    try {
      let endpoint = '';
      if (tab === 'spitcast') endpoint = `/api/spots/detail/tab/spitcast?name=${encodeURIComponent(name)}`;
      else if (tab === 'tides') endpoint = `/api/spots/detail/tab/noaa?name=${encodeURIComponent(name)}`;
      else if (tab === 'forecast' || tab === 'daily') endpoint = `/api/spots/detail/tab/forecast?name=${encodeURIComponent(name)}`;
      if (!endpoint) return;
      const res = await fetch(`${API}${endpoint}`);
      if (res.ok) {
        const d = await res.json();
        if (tab === 'spitcast') {
          setTabData(prev => ({ ...prev, spitcast: d.spitcast }));
          tabFetched.current.spitcast = true;
        } else if (tab === 'tides') {
          setTabData(prev => ({ ...prev, noaa: d.noaa }));
          tabFetched.current.noaa = true;
        } else if (tab === 'forecast' || tab === 'daily') {
          setTabData(prev => ({ ...prev, forecast: d }));
          tabFetched.current.forecast = true;
          tabFetched.current.daily = true;
        }
      }
    } catch {} finally {
      setTabLoading(prev => ({ ...prev, [tab]: false }));
    }
  }, [name, tabLoading]);

  useEffect(() => {
    if (activeTab !== 'overview' && data) {
      loadTabData(activeTab);
    }
  }, [activeTab, data, loadTabData]);

  if (loading) return <LoadingDots />;
  if (!data) return <p className="p-5 text-xs text-red-400">Failed to load spot data</p>;

  const spot = data.spot || {};
  const windy = data.windy || {};
  const windyCurrent = windy.current || {};
  const windyForecast = windy.forecast || [];
  const openMeteo = data.open_meteo || {};
  const omCurrent = openMeteo.current || {};
  const omHourly = openMeteo.hourly || [];
  const omDaily = openMeteo.daily || [];
  const omWeatherDaily = openMeteo.weather_daily || [];
  const webcams = data.webcams || [];
  const spitcast = tabData.spitcast || data.spitcast || null;
  const noaa = tabData.noaa || data.noaa || null;
  const hasWindy = windyCurrent.wave_height_ft !== undefined;
  const hasOM = omCurrent.wave_height_ft !== undefined;
  const hasSpitcast = !!(data.spot?.spitcast_id);
  const spitcastLoaded = spitcast && spitcast.hours && spitcast.hours.length > 0;
  const noaaLoaded = noaa && noaa.tides && noaa.tides.length > 0;
  const alerts = data.alerts || [];
  const bestTime = data.best_time || null;
  const mappedCams = data.mapped_cams || [];
  const hasAlerts = alerts.length > 0;
  const current = hasWindy ? { ...windyCurrent, air_temp_f: omCurrent.air_temp_f, wind_speed_kph: omCurrent.wind_speed_kph || windyCurrent.wind_speed_kph, wind_direction: omCurrent.wind_direction || windyCurrent.wind_direction, sea_surface_temp_c: omCurrent.sea_surface_temp_c, weather_code: omCurrent.weather_code } : omCurrent;
  const summary = hasWindy ? windy.summary : (current.wave_height_ft ? `${current.wave_height_ft < 3 ? 'small' : current.wave_height_ft < 5 ? 'fun-size' : 'solid'}` : '');
  const sources = [];
  if (hasWindy) sources.push('Windy');
  if (hasOM) sources.push('Open-Meteo');
  if (hasSpitcast) sources.push('Spitcast');
  sources.push('NOAA');
  if (hasAlerts) sources.push('NWS');

  const nextTide = noaaLoaded ? noaa.tides.find(t => { const d = new Date(t.time.replace(' ', 'T')); return d > new Date(); }) : null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'forecast', label: 'Forecast' },
    ...(hasSpitcast ? [{ id: 'spitcast', label: 'Spitcast' }] : []),
    { id: 'tides', label: 'Tides' },
    { id: 'daily', label: 'Multi-Day' },
  ];

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-5" data-testid="spot-detail">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={onClose} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors" data-testid="spot-detail-back">
          <ChevronLeft className="w-4 h-4" /> All Spots
        </button>
        <div className="flex items-center gap-2">
          <ReactionButtons spotName={name} myReaction={myReaction} onReact={onReact} />
          <SpotMeta lat={spot.lat} lon={spot.lon} name={name} />
          <button data-testid="spot-detail-star" onClick={() => onStar(name)}
            className={`p-1.5 rounded-xl border transition-all hover:bg-white/5 ${isStarred ? 'border-yellow-400/30 text-yellow-400' : 'border-white/10 text-muted-foreground/40'}`}>
            <Star className={`w-3.5 h-3.5 ${isStarred ? 'fill-yellow-400' : ''}`} />
          </button>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center gap-1.5">
          <h2 className="font-heading text-xl font-bold">{spot.name}</h2>
          {spot.verified && (
            <span className="relative group" data-testid="spot-detail-verified-badge">
              <BadgeCheck className="w-5 h-5 text-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.4)]" />
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[9px] font-medium bg-amber-400/90 text-black rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Community Verified
              </span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{spot.region}</p>
          {spot.difficulty && <span className={`text-[9px] px-2 py-0.5 rounded-md font-medium ${DIFFICULTY_COLORS[spot.difficulty] || DIFFICULTY_COLORS.all}`}>{spot.difficulty}</span>}
          {spot.source === 'community' && <span className="text-[8px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400">community</span>}
          {spot.source === 'seed' && <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400">Seed</span>}
          {spot.source === 'spitcast' && <span className="text-[8px] px-1.5 py-0.5 rounded bg-primary/15 text-primary">Spitcast</span>}
        </div>
        {spot.wave_type && <p className="text-[10px] text-muted-foreground/60 mt-1">{spot.wave_type}</p>}
      </div>

      {/* Ask Swell about this spot */}
      <button
        data-testid="spot-chat-swell-btn"
        onClick={() => {
          const waterF = noaa?.water_temp?.temp_f || (current?.sea_surface_temp_c > 0 ? Math.round(current.sea_surface_temp_c * 9/5 + 32) : 0);
          const conditions = current ? ` Current conditions: ${current.wave_height_ft ? current.wave_height_ft + 'ft waves' : ''}${current.swell_period_s ? ', ' + current.swell_period_s + 's period' : ''}${current.wind_speed_kph ? ', wind ' + current.wind_speed_kph + 'kph' : ''}${current.air_temp_f ? ', air ' + current.air_temp_f + 'F' : ''}${waterF ? ', water ' + waterF + 'F' : ''}.` : '';
          const msg = `Tell me about ${spot.name}${spot.region ? ' in ' + spot.region : ''} — is it worth paddling out right now? What suit should I wear?${conditions}`;
          navigate('/chat', { state: { prefillMessage: msg } });
        }}
        className="w-full mb-3 bg-primary/[0.07] border border-primary/20 hover:border-primary/40 rounded-xl px-3 py-2.5 flex items-center gap-2.5 transition-all group"
      >
        <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/25 transition-colors">
          <MessageCircle className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="text-left">
          <p className="text-[11px] font-semibold text-primary">Ask Swell about this spot</p>
          <p className="text-[9px] text-muted-foreground/40">Get a personalized surf report & advice</p>
        </div>
      </button>

      {/* Address + Coords + Directions */}
      <div className="bg-bg-subtle/60 border border-white/5 rounded-xl p-3 mb-3 space-y-1.5">
        {spot.address && <p className="text-[11px] text-muted-foreground">{spot.address}</p>}
        <p className="text-[10px] text-muted-foreground/50 font-mono">{spot.lat?.toFixed(4)}, {spot.lon?.toFixed(4)}</p>
        {spot.lat && spot.lon && (
          <button data-testid="spot-directions-btn" onClick={() => openDirections(spot.lat, spot.lon, spot.name)}
            className="flex items-center gap-1.5 text-[11px] text-primary font-medium hover:underline mt-1">
            <MapPin className="w-3.5 h-3.5" /> Get Directions
          </button>
        )}
      </div>

      {/* Data Sources */}
      <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="spot-data-sources">
        <span className="text-[9px] text-muted-foreground/50 mr-1">Sources:</span>
        <DataSourceBadge label="Windy" active={hasWindy} />
        <DataSourceBadge label="Open-Meteo" active={hasOM} />
        <DataSourceBadge label="Spitcast" active={hasSpitcast} />
        <DataSourceBadge label="NOAA" active />
        {hasAlerts && <DataSourceBadge label="NWS Alerts" active />}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-bg-subtle/50 rounded-xl p-1 overflow-x-auto scrollbar-hide" data-testid="spot-tabs">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 px-3 text-[10px] py-1.5 rounded-lg font-medium transition-all ${activeTab === tab.id ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`spot-tab-${tab.id}`}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <SpotOverviewTab
          current={current} summary={summary} hasWindy={hasWindy} hasOM={hasOM}
          noaaLoaded={noaaLoaded} noaa={noaa} nextTide={nextTide}
          alerts={alerts} hasAlerts={hasAlerts}
          bestTime={bestTime}
          mappedCams={mappedCams} webcams={webcams}
        />
      )}
      {activeTab === 'forecast' && (
        <SpotForecastTab windyForecast={windyForecast} omHourly={omHourly} tabData={tabData} tabLoading={tabLoading} />
      )}
      {activeTab === 'spitcast' && (
        <SpotSpitcastTab spitcast={spitcast} spitcastLoaded={spitcastLoaded} tabLoading={tabLoading} />
      )}
      {activeTab === 'tides' && (
        <SpotTidesTab noaa={noaa} noaaLoaded={noaaLoaded} tabLoading={tabLoading} />
      )}
      {activeTab === 'daily' && (
        <SpotDailyTab omDaily={omDaily} omWeatherDaily={omWeatherDaily} tabData={tabData} tabLoading={tabLoading} />
      )}

      <p className="text-[9px] text-muted-foreground/40 text-center mt-4">Data from {sources.length > 0 ? sources.join(' & ') : 'Open-Meteo'}</p>
    </motion.div>
  );
}
